/*  LandingCms — Admin CMS for the CounsellorCallbackCard (and future
    landing-page surfaces).
    ────────────────────────────────────────────────────────────────
    Three tabs:
      • Counsellors — pick who's featured, edit their landing profile,
                      flip is_available_now
      • Content     — key/value editor for every CMS string
      • Settings    — reply time, callback SLA, call duration,
                      operating hours + days

    Everything saves straight to the DB and reflects on the live card
    within the next fetch — no deploy required.
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Star, CheckCircle, Circle, PencilSimple, Warning, ArrowsClockwise,
    User as UserIcon, FloppyDisk, Info, CaretRight, CaretDown,
    ArrowSquareOut, ArrowCounterClockwise, ClockCounterClockwise,
} from "@phosphor-icons/react";
import HeroImageUploader from "./HeroImageUploader";
import TestimonialsTab from "./TestimonialsTab";
import MediaLibraryTab from "./MediaLibraryTab";
import WhyInvisiqueTab from "./WhyInvisiqueTab";
import PatientSpotlightsTab from "./PatientSpotlightsTab";
import AdminSegmentedTabs from "@/components/admin/AdminSegmentedTabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";


// Weekday chip options — keep aligned with backend defaults so admin
// clicks always produce validated input.
const DAY_OPTIONS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];


/* ─── Page grouping & labels for the CMS page selector ───────────
   iter100.145 — Rather than surface a flat wall of raw slugs
   (`counsellor_card`, `product_secure`, `nav_products` …), the admin
   sees the same list split into three intuitive groups with
   human-readable labels + a short helper line explaining what
   each surface controls. */
const CMS_PAGE_GROUPS = [
    {
        id: "landing_pages",
        title: "Landing Pages",
        hint: "Full marketing pages a visitor can land on directly.",
        pages: {
            product_secure:   { label: "Night Guard",         subtitle: "Invisique Secure page",   previewPath: "/mouthguards-and-retainer/night-guard" },
            product_serene:   { label: "Anti-Snoring Guard",  subtitle: "Invisique Serene page",   previewPath: "/mouthguards-and-retainer/anti-snoring" },
            product_apex:     { label: "Sports Guard",        subtitle: "Invisique Apex page",     previewPath: "/mouthguards-and-retainer/sports-guard" },
            product_infinite: { label: "Retainer",            subtitle: "Invisique Infinite page", previewPath: "/mouthguards-and-retainer/retainer" },
            pricing:          { label: "Pricing Page",        subtitle: "/pricing — hero, complexity tiers, cost factors, FAQs, testimonial", previewPath: "/pricing" },
            faq_page:         { label: "FAQ Page",             subtitle: "/faq — full categorised FAQ library",                             previewPath: "/faq" },
            contact:          { label: "Contact Page",        subtitle: "SEO meta for /contact",   previewPath: "/contact" },
            home:             { label: "Homepage (SEO)",      subtitle: "Homepage document title & meta", previewPath: "/" },
        },
    },
    {
        id: "homepage_sections",
        title: "Homepage Sections",
        hint: "Individual sections that stack together on the homepage.",
        pages: {
            hero:              { label: "Hero (Homepage Top)",     subtitle: "Above-the-fold headline & CTAs" },
            counsellor_card:   { label: "Counsellor Callback Card", subtitle: "Homepage lead-capture card" },
            doctor_benefits:   { label: "Doctor Benefits Strip",   subtitle: 'The "For Doctors" strip on the homepage' },
            faq:               { label: "FAQ Section",             subtitle: "Homepage frequently-asked questions" },
        },
    },
    {
        id: "global_components",
        title: "Global Components",
        hint: "Shared UI that renders on many pages at once.",
        pages: {
            announcement_bar:  { label: "Announcement Bar",     subtitle: "Top-of-page marketing strip (on/off)" },
            nav_products:      { label: "Products Mega-Menu",   subtitle: "Header dropdown across the site" },
            footer:            { label: "Site Footer",          subtitle: "Bottom of every marketing page" },
            contact_info:      { label: "Contact Info",         subtitle: "Phone / email / address / hours (Footer + Contact)" },
            social_links:      { label: "Social Links",         subtitle: "Instagram / LinkedIn / X / YouTube URLs" },
        },
    },
    {
        id: "legal_pages",
        title: "Legal Pages",
        hint: "Long-form policy documents. Body is raw HTML — edit with care.",
        pages: {
            legal_privacy: { label: "Privacy Policy",           subtitle: "/privacy",         previewPath: "/privacy" },
            legal_terms:   { label: "Terms of Service",         subtitle: "/terms",           previewPath: "/terms" },
            legal_refund:  { label: "Refund & Cancellation",    subtitle: "/refund-policy",   previewPath: "/refund-policy" },
        },
    },
];

/* Fallback label helper — for any page slug not explicitly mapped above,
   fall back to a "prettier" version of the raw slug. */
function labelForPage(pageSlug) {
    for (const g of CMS_PAGE_GROUPS) {
        if (g.pages[pageSlug]) return g.pages[pageSlug].label;
    }
    return pageSlug.replace(/_/g, " ");
}

function previewPathForPage(pageSlug) {
    for (const g of CMS_PAGE_GROUPS) {
        if (g.pages[pageSlug]?.previewPath) return g.pages[pageSlug].previewPath;
    }
    return null;
}


export default function LandingCms() {
    const [tab, setTab] = useState("content");
    return (
        <div className="space-y-6" data-testid="landing-cms-page">
            <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Content · Marketing</div>
                <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">Landing Page Content</h1>
                <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                    Every string, image, and SLA on the public marketing site — live in seconds, no deploy.
                </p>
            </div>

            <AdminSegmentedTabs
                testid="landing-cms-tabs"
                tabTestidBase="tab"
                active={tab}
                onChange={setTab}
                tabs={[
                    { id: "content",         label: "Content" },
                    { id: "counsellor-card", label: "Counsellor Card" },
                    { id: "testimonials",    label: "Testimonials" },
                    { id: "spotlights",      label: "Patient Spotlights" },
                    { id: "why-invisique",   label: "Why Invisique" },
                    { id: "media",           label: "Media Library" },
                    { id: "activity",        label: "Activity" },
                ]}
            />

            <div>
                {tab === "content"         && <ContentTab />}
                {tab === "counsellor-card" && <CounsellorCardTab />}
                {tab === "testimonials"    && <TestimonialsTab />}
                {tab === "spotlights"      && <PatientSpotlightsTab />}
                {tab === "why-invisique"   && <WhyInvisiqueTab />}
                {tab === "media"           && <MediaLibraryTab />}
                {tab === "activity"        && <ActivityTab />}
            </div>
        </div>
    );
}


/* ─── Counsellor Card Tab — merged Counsellors + Settings ────────
   Everything on this tab drives the homepage Counsellor Callback
   Card: WHO is featured, HOW available they are, and the SLA
   numbers / operating hours that render on the card itself. */
function CounsellorCardTab() {
    return (
        <div className="space-y-6" data-testid="counsellor-card-tab">
            <SectionHeader
                overline="Featured counsellor"
                title="Who shows up on the homepage"
                description="Spotlight one counsellor, flip availability, or edit their landing profile."
            />
            <CounsellorsTab />
            <SectionHeader
                overline="Card SLAs & hours"
                title="Reply time, callback SLA & operating hours"
                description="These numbers render live on the card (reply badge, trust footer, done-screen subtitle)."
            />
            <SettingsTab />
        </div>
    );
}


function SectionHeader({ overline, title, description }) {
    return (
        <div className="border-l-2 border-primary/30 pl-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">{overline}</div>
            <div className="font-display text-lg font-bold text-foreground mt-0.5">{title}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{description}</div>
        </div>
    );
}


/* ─── ActivityTab — CMS edit log (iter100.147) ────────────────────
   Read-only stream of the most recent edits. Fed by audit_logs rows
   inserted by PUT /admin/landing/cms + POST /cms/reset. */
function ActivityTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/landing/cms/activity", { params: { limit: 100 } });
            setItems(data.items || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load activity.");
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const relTime = (iso) => {
        if (!iso) return "";
        const then = new Date(iso).getTime();
        const diff = Date.now() - then;
        const min = Math.round(diff / 60000);
        if (min < 1) return "just now";
        if (min < 60) return `${min}m ago`;
        const hr = Math.round(min / 60);
        if (hr < 24) return `${hr}h ago`;
        const day = Math.round(hr / 24);
        if (day < 30) return `${day}d ago`;
        return new Date(iso).toLocaleDateString();
    };

    const actionLabel = (a) => {
        if (a === "cms_updated") return { text: "edited", color: "text-primary" };
        if (a === "cms_reset") return { text: "reset key", color: "text-rose-600" };
        if (a === "cms_reset_section") return { text: "reset section", color: "text-rose-600" };
        if (a === "cms_reset_page") return { text: "reset page", color: "text-rose-600" };
        return { text: a, color: "text-muted-foreground" };
    };

    return (
        <div className="space-y-4" data-testid="activity-tab">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary flex items-center gap-1">
                        <ClockCounterClockwise size={11} weight="bold" />
                        Recent CMS edits
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                        The last {items.length || "few"} content changes — who edited what and when.
                    </div>
                </div>
                <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="activity-refresh">
                    <ArrowsClockwise size={11} weight="bold" className="mr-1" />
                    Refresh
                </Button>
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground py-8 text-center">Loading activity…</div>
            ) : items.length === 0 ? (
                <div className="text-sm text-muted-foreground py-8 text-center bg-secondary/20 rounded-2xl">
                    No CMS edits yet. Once you save a change on the Content tab, it will show up here.
                </div>
            ) : (
                <ul className="space-y-2" data-testid="activity-list">
                    {items.map((it) => {
                        const al = actionLabel(it.action);
                        return (
                            <li
                                key={it.id}
                                className="bg-card border border-border rounded-xl p-3 flex items-start gap-3"
                                data-testid={`activity-item-${it.id}`}
                            >
                                <div className="w-8 h-8 rounded-full bg-primary/10 grid place-items-center shrink-0">
                                    <UserIcon size={14} weight="fill" className="text-primary" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs">
                                        <b className="text-foreground">{it.actor_name || "system"}</b>
                                        <span className={`ml-1 font-semibold ${al.color}`}>{al.text}</span>
                                        <span className="ml-1 text-muted-foreground">·</span>
                                        <code className="ml-1 px-1.5 py-0.5 rounded bg-secondary/60 text-[10.5px] font-mono text-foreground/80 break-all">{it.entity_id}</code>
                                    </div>
                                    {it.summary && (
                                        <div className="mt-1 text-[11px] text-muted-foreground line-clamp-2">
                                            {it.summary}
                                        </div>
                                    )}
                                </div>
                                <div className="text-[10.5px] text-muted-foreground shrink-0 pt-0.5" title={it.created_at}>
                                    {relTime(it.created_at)}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}


/* ─── Counsellors Tab ────────────────────────────────────────────── */
function CounsellorsTab() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);       // staff_id currently in the edit drawer

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/landing/counsellors");
            setRows(data || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load counsellors.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const setFeatured = async (staffId) => {
        try {
            await api.post(`/admin/landing/counsellors/${staffId}/feature`);
            toast.success("Featured counsellor updated.");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't update.");
        }
    };

    const toggleAvail = async (staffId, next) => {
        try {
            await api.patch(`/admin/landing/counsellors/${staffId}/availability`, { is_available_now: next });
            toast.success(next ? "Marked available." : "Marked offline.");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't update availability.");
        }
    };

    if (loading) return <div className="text-sm text-muted-foreground py-8 text-center">Loading counsellors…</div>;
    if (rows.length === 0) return (
        <div className="py-16 text-center border border-dashed border-border rounded-2xl">
            <Warning size={28} weight="duotone" className="text-amber-600 mx-auto mb-2" />
            <div className="font-semibold">No counsellors on file</div>
            <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
                Add a CoE staff member with role &ldquo;counsellor&rdquo; from Team &amp; Access, then come back to spotlight them here.
            </p>
        </div>
    );

    return (
        <div className="space-y-3" data-testid="counsellors-list">
            <div className="text-[11px] text-muted-foreground bg-primary/5 border border-primary/20 rounded-lg px-3 py-2 flex items-start gap-2">
                <Info size={12} weight="fill" className="text-primary shrink-0 mt-0.5" />
                <div>The <b>featured</b> counsellor appears on the homepage card when they&apos;re marked available. If they go offline, the card falls back to the next available counsellor, then to an offline message.</div>
            </div>

            {rows.map((row) => (
                <CounsellorRow
                    key={row.staff_id}
                    row={row}
                    onFeature={() => setFeatured(row.staff_id)}
                    onToggleAvail={(v) => toggleAvail(row.staff_id, v)}
                    onEdit={() => setEditing(row.staff_id)}
                />
            ))}

            {editing && (
                <CounsellorEditDialog
                    row={rows.find((r) => r.staff_id === editing)}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
        </div>
    );
}


function CounsellorRow({ row, onFeature, onToggleAvail, onEdit }) {
    return (
        <div className="bg-card border border-border rounded-2xl p-4 flex items-center gap-4 flex-wrap" data-testid={`counsellor-row-${row.staff_id}`}>
            <div className="shrink-0">
                {row.photo_url ? (
                    <img src={row.photo_url} alt={row.name} className="w-14 h-14 rounded-full object-cover ring-2 ring-primary/10" />
                ) : (
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary/60 to-primary grid place-items-center text-white font-display font-bold">
                        {(row.name || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("")}
                    </div>
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm" data-testid={`counsellor-row-name-${row.staff_id}`}>{row.name}</span>
                    {row.is_featured && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[9px] font-bold uppercase tracking-wider" data-testid={`counsellor-row-featured-${row.staff_id}`}>
                            <Star size={9} weight="fill" /> Featured
                        </span>
                    )}
                    {row.rating > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-amber-600 text-[11px] font-semibold">
                            <Star size={10} weight="fill" /> {row.rating.toFixed(1)} ({row.ratings_count})
                        </span>
                    )}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                    {row.credentials || <span className="italic">No credentials set</span>}
                    {row.designation ? ` · ${row.designation}` : ""}
                </div>
                {row.languages?.length > 0 && (
                    <div className="text-[10px] text-muted-foreground mt-0.5">Speaks {row.languages.join(" · ")}</div>
                )}
            </div>
            <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-[11px] font-semibold cursor-pointer">
                    <Switch
                        checked={row.is_available_now}
                        onCheckedChange={onToggleAvail}
                        data-testid={`counsellor-row-avail-${row.staff_id}`}
                    />
                    <span className={row.is_available_now ? "text-emerald-700" : "text-muted-foreground"}>
                        {row.is_available_now ? "Available" : "Offline"}
                    </span>
                </label>
                {!row.is_featured && (
                    <Button variant="outline" size="sm" onClick={onFeature} data-testid={`counsellor-row-feature-${row.staff_id}`}>
                        <Star size={11} weight="duotone" className="mr-1" /> Feature
                    </Button>
                )}
                <Button variant="outline" size="sm" onClick={onEdit} data-testid={`counsellor-row-edit-${row.staff_id}`}>
                    <PencilSimple size={11} weight="duotone" className="mr-1" /> Edit
                </Button>
            </div>
        </div>
    );
}


function CounsellorEditDialog({ row, onClose, onSaved }) {
    // iter101.39 — Trimmed to admin-only levers. PES-owned fields
    // (credentials, designation, languages, bio) are now edited by the
    // counsellor from their My Profile page; this dialog previews them
    // read-only so admins can spot-check what will render publicly.
    const [form, setForm] = useState({
        rating: row.rating || 0,
        ratings_count: row.ratings_count || 0,
        is_available_now: !!row.is_available_now,
        sort_order: row.sort_order || 0,
    });
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            // Two-shot: admin-only fields go to the landing profile PUT;
            // the availability toggle uses the dedicated endpoint so it
            // shares the exact same code path as the counsellor's own
            // dashboard toggle (single source of truth).
            await Promise.all([
                api.put(`/admin/landing/counsellors/${row.staff_id}/profile`, {
                    rating: Number(form.rating) || 0,
                    ratings_count: Number(form.ratings_count) || 0,
                    sort_order: Number(form.sort_order) || 0,
                }),
                api.patch(`/admin/landing/counsellors/${row.staff_id}/availability`, {
                    is_available_now: form.is_available_now,
                }),
            ]);
            toast.success(`${row.name}'s homepage settings saved.`);
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save.");
        } finally { setSaving(false); }
    };

    const languagesText = (row.languages || []).join(", ");

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
            <div
                className="bg-card rounded-2xl border border-border shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
                data-testid="counsellor-edit-dialog"
            >
                <div className="p-5 border-b border-border sticky top-0 bg-card">
                    <div className="font-display text-xl font-bold">Homepage settings</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{row.name} · {row.email || row.phone}</div>
                </div>
                <div className="p-5 space-y-5">
                    {/* Admin-only levers */}
                    <div className="space-y-3">
                        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Admin controls</div>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Rating (0-5)" hint="Curated · shown as stars">
                                <Input type="number" min="0" max="5" step="0.1" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} data-testid="fe-rating" />
                            </Field>
                            <Field label="Ratings count" hint="Total reviews">
                                <Input type="number" min="0" value={form.ratings_count} onChange={(e) => setForm({ ...form, ratings_count: e.target.value })} data-testid="fe-ratings-count" />
                            </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-3 items-end">
                            <Field label="Sort order" hint="Tie-breaker when multiple online">
                                <Input type="number" min="0" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} data-testid="fe-sort" />
                            </Field>
                            <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer h-10">
                                <Switch checked={form.is_available_now} onCheckedChange={(v) => setForm({ ...form, is_available_now: v })} data-testid="fe-avail" />
                                {form.is_available_now ? "Available now" : "Offline"}
                            </label>
                        </div>
                    </div>

                    {/* Read-only preview of PES-owned fields */}
                    <div className="rounded-xl border border-border bg-secondary/40 p-4 space-y-3" data-testid="counsellor-public-preview">
                        <div className="flex items-start justify-between gap-3">
                            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Public profile · read-only</div>
                            <span className="text-[9.5px] text-muted-foreground italic">Edited by {row.name.split(" ")[0]} in My Profile</span>
                        </div>
                        <ReadOnlyField label="Credentials" value={row.credentials} placeholder="— not set —" />
                        <ReadOnlyField label="Designation" value={row.designation} placeholder="— not set —" />
                        <ReadOnlyField label="Languages" value={languagesText} placeholder="— none —" />
                        <ReadOnlyField label="Public bio" value={row.bio} placeholder="— empty —" multiline />
                    </div>
                </div>
                <div className="p-4 border-t border-border flex justify-end gap-2 sticky bottom-0 bg-card">
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button onClick={save} disabled={saving} data-testid="fe-save">
                        <FloppyDisk size={13} className="mr-1.5" />
                        {saving ? "Saving…" : "Save settings"}
                    </Button>
                </div>
            </div>
        </div>
    );
}


function ReadOnlyField({ label, value, placeholder, multiline }) {
    const isEmpty = !value;
    return (
        <div className="space-y-1">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
            <div
                className={`text-[12.5px] ${isEmpty ? "text-muted-foreground/60 italic" : "text-foreground"} ${multiline ? "line-clamp-3 whitespace-pre-line" : "truncate"}`}
                data-testid={`ro-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
                {isEmpty ? placeholder : value}
            </div>
        </div>
    );
}


/* ─── Content Tab ────────────────────────────────────────────────── */
function ContentTab() {
    const [pages, setPages] = useState([]);
    const [selectedPage, setSelectedPage] = useState("counsellor_card");
    const [entries, setEntries] = useState([]);
    const [drafts, setDrafts] = useState({});         // key -> local edited value
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Fetch the list of pages once so admin sees Hero/FAQ/Footer/etc dynamically.
    useEffect(() => {
        api.get("/admin/landing/cms/pages")
            .then((r) => { setPages(r.data || []); })
            .catch(() => {});
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/landing/cms", { params: { page: selectedPage } });
            setEntries(data.entries || []);
            setDrafts({});
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load content.");
        } finally { setLoading(false); }
    }, [selectedPage]);

    useEffect(() => { load(); }, [load]);

    const dirtyKeys = useMemo(
        () => entries.filter((e) => drafts[e.key] != null && drafts[e.key] !== e.value).map((e) => e.key),
        [entries, drafts],
    );

    const saveAll = async () => {
        if (dirtyKeys.length === 0) return;
        setSaving(true);
        try {
            await api.put("/admin/landing/cms", {
                entries: dirtyKeys.map((k) => ({
                    key: k,
                    value: drafts[k],
                    description: entries.find((e) => e.key === k)?.description || "",
                })),
            });
            toast.success(`Saved ${dirtyKeys.length} change${dirtyKeys.length === 1 ? "" : "s"}. Live in seconds.`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save.");
        } finally { setSaving(false); }
    };

    /* iter100.147 — Reset helpers.
       resetPage()    → wipe every admin edit on the current page.
       resetSection() → wipe every admin edit under one section prefix
                        (e.g. `hero`, `benefits`).
       Both call POST /api/admin/landing/cms/reset which restores
       from the seeded defaults in code. */
    const resetPage = async () => {
        if (!window.confirm(`Reset EVERY field on "${labelForPage(selectedPage)}" back to the shipped default? Any admin edits on this page will be lost.`)) return;
        setSaving(true);
        try {
            const { data } = await api.post("/admin/landing/cms/reset", { page: selectedPage });
            toast.success(`Reset ${data?.reset_count || 0} field${data?.reset_count === 1 ? "" : "s"} to default.`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't reset.");
        } finally { setSaving(false); }
    };
    const resetSection = async (sectionShortKey) => {
        const sectionPath = `${selectedPage}.${sectionShortKey}`;
        if (!window.confirm(`Reset every field under "${sectionShortKey.replace(/_/g, " ")}" back to the shipped default?`)) return;
        setSaving(true);
        try {
            const { data } = await api.post("/admin/landing/cms/reset", { section: sectionPath });
            toast.success(`Reset ${data?.reset_count || 0} field${data?.reset_count === 1 ? "" : "s"} in this section.`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't reset section.");
        } finally { setSaving(false); }
    };

    if (loading) return <div className="text-sm text-muted-foreground py-8 text-center">Loading content…</div>;

    return (
        <div className="space-y-3" data-testid="content-editor">
            {/* iter100.145 — Grouped page selector replaces the old
                flat chip strip. Pages are bucketed into three
                buckets (Landing Pages / Homepage Sections / Global
                Components) with human-readable labels + a short
                helper line so admins immediately understand what
                each surface controls. */}
            {pages.length > 0 && (
                <div className="space-y-2.5" data-testid="content-page-selector">
                    {CMS_PAGE_GROUPS.map((group) => {
                        const groupPages = Object.keys(group.pages).filter((p) => pages.includes(p));
                        if (groupPages.length === 0) return null;
                        return (
                            <div
                                key={group.id}
                                className="bg-card border border-border rounded-2xl p-3"
                                data-testid={`content-page-group-${group.id}`}
                            >
                                <div className="flex items-baseline gap-2 mb-2 px-1">
                                    <span
                                        className="text-[10px] font-bold uppercase tracking-[0.14em] text-primary"
                                        data-testid={`content-page-group-title-${group.id}`}
                                    >
                                        {group.title}
                                    </span>
                                    <span className="text-[10.5px] text-muted-foreground">{group.hint}</span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {groupPages.map((p) => {
                                        const meta = group.pages[p];
                                        const active = selectedPage === p;
                                        return (
                                            <button
                                                key={p}
                                                type="button"
                                                onClick={() => setSelectedPage(p)}
                                                className={`text-left px-3 py-2 rounded-xl border transition-all ${
                                                    active
                                                        ? "bg-primary border-primary text-white shadow-sm"
                                                        : "bg-card border-border hover:border-primary/40 hover:bg-primary/[0.03]"
                                                }`}
                                                data-testid={`content-page-${p}`}
                                            >
                                                <div className={`text-xs font-bold ${active ? "text-white" : "text-foreground"}`}>
                                                    {meta.label}
                                                </div>
                                                <div className={`text-[10px] mt-0.5 ${active ? "text-white/80" : "text-muted-foreground"}`}>
                                                    {meta.subtitle}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                    {/* Any page slug the DB has that we haven't grouped yet.
                        Prevents new backend-seeded pages from silently
                        disappearing from the UI. */}
                    {(() => {
                        const known = new Set(CMS_PAGE_GROUPS.flatMap((g) => Object.keys(g.pages)));
                        const orphans = pages.filter((p) => !known.has(p));
                        if (orphans.length === 0) return null;
                        return (
                            <div className="bg-card border border-dashed border-border rounded-2xl p-3">
                                <div className="flex items-baseline gap-2 mb-2 px-1">
                                    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                                        Other
                                    </span>
                                    <span className="text-[10.5px] text-muted-foreground">
                                        Pages seeded by the backend that haven&apos;t been grouped yet.
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {orphans.map((p) => (
                                        <button
                                            key={p}
                                            type="button"
                                            onClick={() => setSelectedPage(p)}
                                            className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
                                                selectedPage === p
                                                    ? "bg-primary border-primary text-white"
                                                    : "bg-card border-border text-muted-foreground hover:bg-secondary"
                                            }`}
                                            data-testid={`content-page-${p}`}
                                        >
                                            {p.replace(/_/g, " ")}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}
                </div>
            )}
            <div className="flex items-center justify-between gap-2 flex-wrap sticky top-0 bg-secondary/30 py-2 z-10" data-testid="content-toolbar">
                <div className="text-[11px] text-muted-foreground">
                    Editing <b className="text-foreground" data-testid="content-current-page-label">{labelForPage(selectedPage)}</b>
                    <span className="mx-1">·</span>
                    <b className="text-foreground">{entries.length}</b> fields
                    <span className="mx-1">·</span>
                    <b className="text-foreground">{dirtyKeys.length}</b> unsaved
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {previewPathForPage(selectedPage) && (
                        <a
                            href={previewPathForPage(selectedPage)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="content-preview-link"
                            className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-border bg-white text-xs font-semibold hover:border-primary/50 hover:text-primary transition-colors"
                        >
                            <ArrowSquareOut size={11} weight="bold" />
                            Preview
                        </a>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={resetPage}
                        disabled={saving}
                        data-testid="content-reset-page"
                        title={`Reset every field on ${labelForPage(selectedPage)} to the shipped default`}
                    >
                        <ArrowCounterClockwise size={11} weight="bold" className="mr-1" />
                        Reset page
                    </Button>
                    <Button variant="outline" size="sm" onClick={load} disabled={saving}>
                        <ArrowsClockwise size={11} weight="bold" className="mr-1" /> Refresh
                    </Button>
                    <Button size="sm" onClick={saveAll} disabled={saving || dirtyKeys.length === 0} data-testid="content-save-all">
                        <FloppyDisk size={11} weight="fill" className="mr-1" />
                        {saving ? "Saving…" : `Save ${dirtyKeys.length || ""} change${dirtyKeys.length === 1 ? "" : "s"}`}
                    </Button>
                </div>
            </div>
            {entries.length > 0 && (
                <SectionGroupedEditor
                    entries={entries}
                    drafts={drafts}
                    setDrafts={setDrafts}
                    pageSlug={selectedPage}
                    onResetSection={resetSection}
                />
            )}
        </div>
    );
}


/* ─── SectionGroupedEditor ───────────────────────────────────────────
   Groups the flat list of CMS entries into collapsible section cards
   keyed on the prefix before the FIRST dot in the short key
   (e.g. `hero.title_line1` → section "hero"). Product-page slugs
   (product_*) get grouping; the older single-section pages
   (`counsellor_card`, `hero`, `footer`, ...) fall back to a single
   "content" group so the UX is identical to before. */
function SectionGroupedEditor({ entries, drafts, setDrafts, pageSlug, onResetSection }) {
    // The short key strips the redundant `{pageSlug}.` prefix so section
    // detection works on the DB `key` regardless of how it was seeded.
    const shortKey = (fullKey) => (fullKey.startsWith(`${pageSlug}.`) ? fullKey.slice(pageSlug.length + 1) : fullKey);

    // Group ordering (section prefix → position). Falls back to
    // discovery order for anything unlisted.
    const SECTION_ORDER = [
        "hero", "trust", "pain", "benefits", "science", "engineering",
        "sports", "journey", "comparison", "testimonials", "doctor",
        "faq", "related", "final_cta", "content",
    ];

    const groups = useMemo(() => {
        const map = new Map();
        for (const e of entries) {
            const sk = shortKey(e.key);
            const section = sk.includes(".") ? sk.split(".", 1)[0] : "content";
            if (!map.has(section)) map.set(section, []);
            map.get(section).push(e);
        }
        return Array.from(map.entries()).sort(([a], [b]) => {
            const ia = SECTION_ORDER.indexOf(a);
            const ib = SECTION_ORDER.indexOf(b);
            if (ia === -1 && ib === -1) return a.localeCompare(b);
            if (ia === -1) return 1;
            if (ib === -1) return -1;
            return ia - ib;
        });
    }, [entries, pageSlug]);

    // iter100.146 — Accordion behavior: only one section open at a
    // time, all closed by default. Prior behavior (all open, toggle
    // each independently) overwhelmed admins on pages with 6+ sections.
    // State stores a single section id (or null when everything is
    // closed) instead of a per-section boolean map.
    const [openSection, setOpenSection] = useState(null);
    useEffect(() => {
        // Reset to all-closed whenever the underlying group set changes
        // (i.e. admin switches between pages).
        setOpenSection(null);
    }, [groups]);

    return (
        <div className="space-y-3" data-testid="content-groups">
            {groups.map(([section, list]) => {
                const dirtyCount = list.filter((e) => drafts[e.key] != null && drafts[e.key] !== e.value).length;
                const isOpen = openSection === section;
                return (
                    <div key={section} className="bg-secondary/20 border border-border rounded-2xl overflow-hidden" data-testid={`content-group-${section}`}>
                        <div className="flex items-center gap-1 pr-3">
                            <button
                                type="button"
                                onClick={() => setOpenSection(isOpen ? null : section)}
                                className="flex-1 flex items-center gap-2.5 px-4 py-3 hover:bg-secondary/40 transition-colors text-left"
                                data-testid={`content-group-toggle-${section}`}
                                aria-expanded={isOpen}
                            >
                                {isOpen ? <CaretDown size={12} weight="bold" className="text-muted-foreground" /> : <CaretRight size={12} weight="bold" className="text-muted-foreground" />}
                                <div className="font-display font-bold text-sm text-foreground uppercase tracking-wide">
                                    {section.replace(/_/g, " ")}
                                </div>
                                <span className="text-[11px] text-muted-foreground ml-1">
                                    {list.length} {list.length === 1 ? "field" : "fields"}
                                </span>
                                {dirtyCount > 0 && (
                                    <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 border border-primary/25 text-[10px] font-bold uppercase tracking-wider text-primary">
                                        {dirtyCount} unsaved
                                    </span>
                                )}
                            </button>
                            {isOpen && onResetSection && (
                                <button
                                    type="button"
                                    onClick={(ev) => { ev.stopPropagation(); onResetSection(section); }}
                                    className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-border bg-white text-[10.5px] font-semibold text-muted-foreground hover:border-rose-400 hover:text-rose-700 transition-colors"
                                    data-testid={`content-group-reset-${section}`}
                                    title={`Reset every field under "${section}" to its shipped default`}
                                >
                                    <ArrowCounterClockwise size={10} weight="bold" />
                                    Reset section
                                </button>
                            )}
                        </div>
                        {isOpen && (
                            <div className="p-3 space-y-2.5 bg-white/60" data-testid={`content-group-body-${section}`}>
                                {list.map((e) => (
                                    <EntryRow
                                        key={e.key}
                                        entry={e}
                                        shortKey={shortKey(e.key)}
                                        draft={drafts[e.key]}
                                        onChange={(v) => setDrafts((d) => ({ ...d, [e.key]: v }))}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}


/* ─── EntryRow — single field editor ─────────────────────────────
   Detects JSON-array-string values (keys ending in `_json` OR values
   parseable to an Array) and hints the admin that they're editing JSON
   so a bad edit is obvious. Otherwise falls back to the same
   Textarea/Input auto-detection used before. */
function EntryRow({ entry, shortKey, draft, onChange }) {
    const value = draft ?? entry.value;
    const dirty = draft != null && draft !== entry.value;
    const isJson = shortKey.endsWith("_json") || (String(entry.value || "").trim().startsWith("["));
    // Special-case: hero image entries get an inline uploader that
    // writes into `value` on success. The regular textarea stays
    // rendered below so an admin can still hand-paste a URL if needed.
    const isHeroImage = shortKey === "hero.image_url";
    const heroPage = isHeroImage ? entry.page : null;
    const jsonState = useMemo(() => {
        if (!isJson) return null;
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) return { ok: true, count: parsed.length };
        } catch (err) {
            return { ok: false, error: err.message };
        }
        return null;
    }, [isJson, value]);
    const useTextarea = isJson || (value || "").length > 60 || (value || "").includes("\n");
    return (
        <div className={`bg-card border rounded-xl p-3 ${dirty ? "border-primary/50 shadow-[0_0_0_2px_hsl(190_65%_38%/0.12)]" : "border-border"}`} data-testid={`cms-row-${entry.key}`}>
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
                <code className="text-[10.5px] font-mono font-semibold bg-secondary text-foreground px-1.5 py-0.5 rounded">{shortKey}</code>
                {isJson && (
                    <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground border border-border rounded-full px-1.5 py-0.5">JSON array</span>
                )}
                {dirty && <span className="text-[9px] font-bold uppercase tracking-wider text-primary">Unsaved</span>}
                {jsonState && jsonState.ok && (
                    <span className="text-[9.5px] text-emerald-600 font-semibold ml-auto">{jsonState.count} rows</span>
                )}
                {jsonState && !jsonState.ok && (
                    <span className="text-[9.5px] text-rose-600 font-semibold ml-auto">
                        <Warning size={10} weight="fill" className="inline mr-0.5 -mt-0.5" />
                        Invalid JSON — value won&apos;t apply
                    </span>
                )}
            </div>
            {useTextarea ? (
                <Textarea
                    value={value}
                    onChange={(ev) => onChange(ev.target.value)}
                    rows={isJson ? Math.min(14, (value.match(/\n/g) || []).length + 4) : Math.min(6, (value.match(/\n/g) || []).length + 2)}
                    maxLength={20000}
                    className={`text-sm ${isJson ? "font-mono text-[12px] leading-relaxed" : "font-normal"}`}
                    data-testid={`cms-input-${entry.key}`}
                />
            ) : (
                <Input
                    value={value}
                    onChange={(ev) => onChange(ev.target.value)}
                    maxLength={2000}
                    className="text-sm"
                    data-testid={`cms-input-${entry.key}`}
                />
            )}
            {entry.description && (
                <div className="text-[10.5px] text-muted-foreground mt-1.5 leading-snug">{entry.description}</div>
            )}
            {isHeroImage && (
                <HeroImageUploader
                    page={heroPage}
                    value={value}
                    onChange={(newUrl) => onChange(newUrl)}
                />
            )}
        </div>
    );
}


/* ─── Settings Tab ───────────────────────────────────────────────── */
function SettingsTab() {
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/admin/landing/settings")
            .then((r) => setForm({
                reply_time_minutes: r.data.reply_time_minutes || 8,
                callback_sla_minutes: r.data.callback_sla_minutes || 8,
                call_duration_minutes: r.data.call_duration_minutes || 15,
                operating_hours_start: r.data.operating_hours_start || "09:00",
                operating_hours_end: r.data.operating_hours_end || "19:00",
                operating_days: r.data.operating_days || ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
            }))
            .catch(() => toast.error("Couldn't load settings."))
            .finally(() => setLoading(false));
    }, []);

    const toggleDay = (d) => {
        setForm((f) => ({ ...f, operating_days: f.operating_days.includes(d)
            ? f.operating_days.filter((x) => x !== d)
            : [...f.operating_days, d].sort((a, b) => DAY_OPTIONS.indexOf(a) - DAY_OPTIONS.indexOf(b)),
        }));
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/admin/landing/settings", {
                ...form,
                reply_time_minutes: Number(form.reply_time_minutes),
                callback_sla_minutes: Number(form.callback_sla_minutes),
                call_duration_minutes: Number(form.call_duration_minutes),
            });
            toast.success("Settings saved. Live in seconds.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save.");
        } finally { setSaving(false); }
    };

    if (loading || !form) return <div className="text-sm text-muted-foreground py-8 text-center">Loading settings…</div>;

    return (
        <div className="max-w-2xl space-y-6 bg-card border border-border rounded-2xl p-6" data-testid="settings-tab">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="Reply time (min)" hint="How fast the counsellor typically replies">
                    <Input type="number" min="1" max="240" value={form.reply_time_minutes} onChange={(e) => setForm({ ...form, reply_time_minutes: e.target.value })} data-testid="setting-reply" />
                </Field>
                <Field label="Callback SLA (min)" hint="Promise on the card & confirmation">
                    <Input type="number" min="1" max="240" value={form.callback_sla_minutes} onChange={(e) => setForm({ ...form, callback_sla_minutes: e.target.value })} data-testid="setting-sla" />
                </Field>
                <Field label="Call duration (min)" hint="&quot;15-Min Call&quot; number">
                    <Input type="number" min="5" max="120" value={form.call_duration_minutes} onChange={(e) => setForm({ ...form, call_duration_minutes: e.target.value })} data-testid="setting-duration" />
                </Field>
            </div>
            <div className="grid grid-cols-2 gap-3 max-w-md">
                <Field label="Hours start" hint="24-hour HH:MM">
                    <Input value={form.operating_hours_start} onChange={(e) => setForm({ ...form, operating_hours_start: e.target.value })} placeholder="09:00" data-testid="setting-start" />
                </Field>
                <Field label="Hours end" hint="24-hour HH:MM">
                    <Input value={form.operating_hours_end} onChange={(e) => setForm({ ...form, operating_hours_end: e.target.value })} placeholder="19:00" data-testid="setting-end" />
                </Field>
            </div>
            <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Operating days</div>
                <div className="flex flex-wrap gap-2" data-testid="setting-days">
                    {DAY_OPTIONS.map((d) => {
                        const on = form.operating_days.includes(d);
                        return (
                            <button
                                key={d}
                                type="button"
                                onClick={() => toggleDay(d)}
                                className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
                                    on ? "bg-primary border-primary text-white" : "bg-secondary/40 border-border text-muted-foreground hover:bg-secondary"
                                }`}
                                data-testid={`setting-day-${d}`}
                            >
                                {d}
                            </button>
                        );
                    })}
                </div>
            </div>
            <div className="flex justify-end pt-2 border-t border-border">
                <Button onClick={save} disabled={saving} data-testid="setting-save">
                    <FloppyDisk size={13} className="mr-1.5" />
                    {saving ? "Saving…" : "Save settings"}
                </Button>
            </div>
        </div>
    );
}


/* ─── Field wrapper ──────────────────────────────────────────────── */
function Field({ label, hint, children }) {
    return (
        <label className="block space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
            {children}
            {hint && <div className="text-[10px] text-muted-foreground italic">{hint}</div>}
        </label>
    );
}
